import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./DigitalCommerce.css";
import Testimonial from "../../../Testimonial/Testimonial";

const DigitalCommerce = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>Digital Marketing Solutions | E-Marketing Technology Stack</title>
        <meta
          name="description"
          content="Discover our digital Marketing expertise and comprehensive e-Marketing technology stack. We leverage cutting-edge tools to build powerful online shopping experiences."
        />
        <meta
          name="keywords"
          content="digital Marketing, e-Marketing solutions, online shopping technology, e-Marketing tech stack, web development, mobile Marketing, payment gateways, shopping cart solutions"
        />
      </Helmet>

      <div className="digital-commerce-container">
        <section className="dc-hero-section">
          <div className="dc-hero-content">
            <h1 className="dc-hero-title">Digital Marketing Solutions</h1>
            <p className="dc-hero-subtitle">
              Transform your online business with cutting-edge e-marketing
              strategies
            </p>
            <a href="/contact-us">
              <button className="dc-cta-button">Get Started</button>
            </a>
          </div>
          <div className="dc-hero-visual">
            <div className="dc-animated-circle dc-circle-1"></div>
            <div className="dc-animated-circle dc-circle-2"></div>
            <div className="dc-animated-circle dc-circle-3"></div>
          </div>
        </section>

        <section className="dc-services-section">
          <h2 className="dc-section-title">Our Digital Marketing Services</h2>
          <div className="dc-services-grid">
            <div className="dc-service-card">
              <div className="dc-service-icon dc-icon-strategy"></div>
              <h3>E-Marketing Strategy</h3>
              <p>
                Comprehensive planning to optimize your online sales funnel and
                customer journey.
              </p>
            </div>
            <div className="dc-service-card">
              <div className="dc-service-icon dc-icon-platform"></div>
              <h3>Platform Development</h3>
              <p>
                Custom online store development with seamless user experience
                and secure payments.
              </p>
            </div>
            <div className="dc-service-card">
              <div className="dc-service-icon dc-icon-mobile"></div>
              <h3>Mobile Marketing</h3>
              <p>
                Responsive designs and mobile apps to capture the growing mobile
                shopping market.
              </p>
            </div>
            <div className="dc-service-card">
              <div className="dc-service-icon dc-icon-optimization"></div>
              <h3>Conversion Optimization</h3>
              <p>
                Data-driven approaches to increase your conversion rates and
                average order value.
              </p>
            </div>
          </div>
        </section>

        <section className="dc-benefits-section">
          <h2 className="dc-section-title">
            Why Choose Our Digital Marketing Solutions
          </h2>
          <div className="dc-benefits-list">
            <div className="dc-benefit-item">
              <h3>Increased Online Sales</h3>
              <p>
                Our strategies are designed to boost your revenue and maximize
                ROI.
              </p>
            </div>
            <div className="dc-benefit-item">
              <h3>Enhanced Customer Experience</h3>
              <p>
                Create seamless shopping experiences that keep customers coming
                back.
              </p>
            </div>
            <div className="dc-benefit-item">
              <h3>Scalable Solutions</h3>
              <p>
                Grow your business with platforms that expand with your needs.
              </p>
            </div>
          </div>
        </section>

        <section className="dc-case-studies">
          <h2 className="dc-section-title">Success Stories</h2>
          <div className="dc-case-study-slider">
            <div className="dc-case-study">
              <div className="dc-case-study-content">
                <h3>Fashion Retailer Transformation</h3>
                <p>
                  Increased online sales by 167% within 6 months of launching
                  new e-marketing platform.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="dc-cta-section">
          <h2>Ready to Transform Your Digital Marketing Experience?</h2>
          <p>Contact us today for a free consultation</p>
          <a
            href="https://wa.me/919155599968?text=Hi!%20I%E2%80%99m%20interested%20in%20Digital%Marketing%20solutions%E2%80%94store%20setup%2C%20payment%20gateway%2C%20catalog%20%26%20SEO.%20Can%20we%20schedule%20a%20quick%20call%3F"
            target="_blank"
          >
            <button className="dc-cta-button dc-cta-large">
              Schedule a Call
            </button>
          </a>
        </section>
      </div>

      <div className="findemo"></div>
      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalCommerce;
